import { createElement, init } from "@airwallex/components-sdk";
import { Button, Modal, message } from "antd";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  completeAirwallexPaymentMethodSetup,
  setupAirwallexPaymentMethod,
} from "@/api/payment";
import { usePaymentStore } from "@/hooks/usePaymentStore";

type AirwallexCardElement = Awaited<ReturnType<typeof createElement<"card">>>;
interface AirwallexCardConfirmation {
  setupId: string;
  confirm: () => Promise<unknown>;
}

const COMPLETE_POLL_INTERVAL_MS = 2_000;
const COMPLETE_FAILED_STATUSES = new Set([
  "failed",
  "cancelled",
  "declined",
  "expired",
]);

const waitForNextPoll = () =>
  new Promise<void>((resolve) => {
    window.setTimeout(resolve, COMPLETE_POLL_INTERVAL_MS);
  });

interface AirwallexCardFormProps {
  visible: boolean;
  onClose: () => void;
}

const getErrorMessage = (error: any, fallback: string) => {
  if (typeof error === "string") return error;

  return (
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.detail?.error?.message ||
    error?.error?.message ||
    error?.error ||
    error?.message ||
    fallback
  );
};

const getConfirmError = (result: any) => {
  if (!result || typeof result !== "object") return undefined;

  const explicitError =
    result?.error ||
    result?.errors?.[0] ||
    result?.detail?.error ||
    result?.data?.error;

  if (explicitError) {
    return getErrorMessage(explicitError, "Unable to save this card. Please try again.");
  }

  if (["FAILED", "CANCELLED", "DECLINED"].includes(result?.status)) {
    return result?.message || "Unable to save this card. Please try again.";
  }

  return undefined;
};

export default function AirwallexCardForm({
  visible,
  onClose,
}: AirwallexCardFormProps) {
  const { t } = useTranslation();
  const cardContainerRef = useRef<HTMLDivElement>(null);
  const cardElementRef = useRef<AirwallexCardElement | null>(null);
  const confirmationRef = useRef<AirwallexCardConfirmation | null>(null);
  const flowIdRef = useRef(0);
  const onCloseRef = useRef(onClose);
  const translateRef = useRef(t);
  const [isPreparing, setIsPreparing] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [isCardElementMounted, setIsCardElementMounted] = useState(false);
  const reQueryWalletData = usePaymentStore((state) => state.reQueryWalletData);

  useEffect(() => {
    translateRef.current = t;
  }, [t]);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  const destroyCardElement = useCallback(() => {
    cardElementRef.current?.destroy();
    cardElementRef.current = null;
    confirmationRef.current = null;
    setIsCardElementMounted(false);
  }, []);

  useEffect(() => {
    if (!visible) {
      flowIdRef.current += 1;
      destroyCardElement();
      setIsPreparing(false);
      setIsConfirming(false);
      return;
    }

    const flowId = flowIdRef.current + 1;
    flowIdRef.current = flowId;
    const isCurrentFlow = () => flowIdRef.current === flowId;
    let createdElement: AirwallexCardElement | null = null;

    const prepareCardElement = async () => {
      setIsPreparing(true);
      try {
        const setup = await setupAirwallexPaymentMethod();
        const action = setup?.action;

        if (
          !setup?.setupId ||
          action?.type !== "airwallex_card_element" ||
          !action?.env ||
          !action?.confirmOptions
        ) {
          throw new Error("Invalid Airwallex setup response");
        }

        await init({ env: action.env, enabledElements: ["payments"] });
        createdElement = await createElement("card");

        if (!isCurrentFlow() || !cardContainerRef.current) {
          createdElement.destroy();
          return;
        }

        createdElement.mount(cardContainerRef.current);
        cardElementRef.current = createdElement;
        confirmationRef.current = {
          setupId: setup.setupId,
          confirm: () => createdElement!.confirm(action.confirmOptions),
        };
        setIsCardElementMounted(true);
      } catch (error) {
        if (isCurrentFlow()) {
          message.error(
            getErrorMessage(
              error,
              translateRef.current("components.airwallexCardForm.setupFailed", {
                defaultValue: "Unable to load the card form. Please try again.",
              })
            )
          );
        }
      } finally {
        if (isCurrentFlow()) {
          setIsPreparing(false);
        }
      }
    };

    prepareCardElement();

    return () => {
      if (isCurrentFlow()) {
        flowIdRef.current += 1;
      }
      if (createdElement && createdElement !== cardElementRef.current) {
        createdElement.destroy();
      }
      destroyCardElement();
    };
  }, [destroyCardElement, visible]);

  const handleConfirm = async () => {
    const confirmation = confirmationRef.current;
    const flowId = flowIdRef.current;

    if (!confirmation || isConfirming) {
      return;
    }

    setIsConfirming(true);

    try {
      const confirmResult = await confirmation.confirm();
      const confirmError = getConfirmError(confirmResult);

      if (confirmError) {
        message.error(confirmError);
        return;
      }

      while (flowIdRef.current === flowId) {
        let result;

        try {
          result = await completeAirwallexPaymentMethodSetup(
            confirmation.setupId
          );
        } catch {
          if (flowIdRef.current !== flowId) return;
          await waitForNextPoll();
          continue;
        }

        if (flowIdRef.current !== flowId) return;

        const status = result?.status?.toLowerCase();

        if (status === "succeeded") {
          message.success(
            translateRef.current("components.airwallexCardForm.saveSuccess", {
              defaultValue: "Card saved successfully.",
            })
          );
          reQueryWalletData({ andLoading: false, forceRefresh: true });
          onCloseRef.current();
          return;
        }

        if (status && COMPLETE_FAILED_STATUSES.has(status)) {
          message.error(
            getErrorMessage(
              result,
              translateRef.current(
                "components.airwallexCardForm.confirmFailed",
                {
                  defaultValue: "Unable to save this card. Please try again.",
                }
              )
            )
          );
          return;
        }

        await waitForNextPoll();
      }
    } catch (error) {
      if (flowIdRef.current === flowId) {
        message.error(
          getErrorMessage(
            error,
            translateRef.current("components.airwallexCardForm.confirmFailed", {
              defaultValue: "Unable to save this card. Please try again.",
            })
          )
        );
      }
    } finally {
      if (flowIdRef.current === flowId) {
        setIsConfirming(false);
      }
    }
  };

  const handleClose = () => {
    flowIdRef.current += 1;
    onClose();
  };

  return (
    <Modal
      title={t("components.airwallexCardForm.title", {
        defaultValue: "Add payment method",
      })}
      open={visible}
      footer={null}
      onCancel={handleClose}
      destroyOnClose
    >
      <div className="py-5">
        <div
          ref={cardContainerRef}
          className="min-h-[48px] rounded-md border border-[#d9d9d9] px-3 py-2"
        />
        {isPreparing && (
          <p className="mt-3 text-sm text-[#737373]">
            {t("components.airwallexCardForm.loading", {
              defaultValue: "Loading secure card form…",
            })}
          </p>
        )}
      </div>
      <Button
        type="primary"
        className="w-full"
        disabled={!isCardElementMounted || isPreparing}
        loading={isConfirming}
        onClick={handleConfirm}
      >
        {t("components.airwallexCardForm.save", {
          defaultValue: "Save card",
        })}
      </Button>
    </Modal>
  );
}
